/// <reference types="office-js" />

import React, { useState, useEffect, useRef } from "react";
import {
  Tree,
  TreeItem,
  TreeItemLayout,
  Link,
  makeStyles,
  Button as FluentButton,
  Spinner,
  Input,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Tooltip,
} from "@fluentui/react-components";
import ConfirmationForm from "./ConfirmationForm";
import { Delete20Regular, Person20Regular } from "@fluentui/react-icons";
import Button from "./Button";
import { fileName } from "../utils";
import api from "../../api/api";
import { getErrorMessage, responseError } from "../../api/apiError";
import { ErrorMessages } from "../../constants/messages";

type ReportsTreeProps = {
  authToken: string;
  identityProvider: string;
  shouldRefresh?: Date;
};

type ReportInstance = {
  id: string;
  name: string;
  url: string;
  token: string;
};

type ReportTemplate = {
  id: string;
  name: string;
  url: string;
  token: string;
  isUserTemplate: boolean;
  isSystemTemplate: boolean;
  instances: ReportInstance[];
};

type ApiReportInstance = {
  id: string;
  name: string;
  url: string;
  sasToken: string;
};

type ApiReportTemplate = {
  id: string;
  name: string;
  url: string;
  sasToken: string;
  isUserTemplate?: boolean;
  isSystemTemplate?: boolean;
  excelFiles?: ApiReportInstance[];
};

type ReportTemplateGroup = {
  id: string;
  name: string;
  templates: ReportTemplate[];
};

const useStyles = makeStyles({
  spinnerContainer: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1000,
    backgroundColor: "rgba(0, 0, 0, 0.4)",
    width: "100vw",
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  reportTemplate: {
    color: "#001C71",
  },
  reportInstance: {
    color: "#6D6E71",
  },
  templateIcon: {
    color: "#6D6E71",
  },
  appIcon: {
    width: "16px",
    height: "16px",
    display: "block",
    filter: "grayscale(1)",
    margin: "2px",
  },
  searchInput: {
    width: "100%",
    fontSize: "inherit",
    border: "0.25pt solid #C4E1E0",
    borderRadius: "6px",
    "&:hover": {
      border: "0.25pt solid #7FBDBA",
    },
  },
  groupLabel: {
    fontWeight: 600,
    color: "#001C71",
  },
  noAnalyzers: {
    color: "#6D6E71",
    padding: "4px 0",
  },
  templateItemLayout: {
    marginLeft: "-24px",
  },
});

const openExcelFromBase64 = async (base64String: string) => {
  await Excel.run(async (context) => {
    Excel.createWorkbook(base64String);
    await context.sync();
  });
};

const fetchAndOpenReportTemplate = async (
  url: string,
  authToken: string,
  identityProvider: string,
  setPercent: (percent: number) => void
) => {
  setPercent(0);
  try {
    await api.fetch(url, authToken, identityProvider).then(async (response) => {
      if (!response.ok) throw await responseError(response, ErrorMessages.TemplateOpenFailed);
      const data: any = await response.json();
      url = data;
    });
    await downloadFileWithProgress(
      url,
      authToken,
      identityProvider,
      setPercent
    ).then(async (fileContent: Uint8Array | null) => {
      if (!fileContent) return;
      const excelContentBase64 = btoa(
        Array.from(fileContent)
          .map((code) => String.fromCharCode(code))
          .join("")
      );
      await openExcelFromBase64(excelContentBase64);
      console.log("Excel report file contents loaded successful.");
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, ErrorMessages.TemplateOpenFailed));
  } finally {
    setPercent(0);
  }
};

const fetchAndOpenReportInstance = async (
  url: string,
  authToken: string,
  identityProvider: string,
  setPercent: (percent: number) => void
) => {
  setPercent(0);
  try {
    await api.fetch(url, authToken, identityProvider).then(async (response) => {
      if (!response.ok) throw await responseError(response, ErrorMessages.ReportOpenFailed);
      const data: any = await response.json();
      url = data;
    });
    await downloadFileWithProgress(
      url,
      authToken,
      identityProvider,
      setPercent
    ).then(async (fileContent: Uint8Array | null) => {
      if (!fileContent) return;
      const excelContentBase64 = btoa(
        Array.from(fileContent)
          .map((code) => String.fromCharCode(code))
          .join("")
      );
      await openExcelFromBase64(excelContentBase64);
      console.log("Excel report instance file contents loaded successful.");
    });
  } catch (error) {
    throw new Error(getErrorMessage(error, ErrorMessages.ReportOpenFailed));
  } finally {
    setPercent(0);
  }
};

const downloadFileWithProgress = async function (
  url: string,
  authToken: string,
  identityProvider: string,
  setPercent: (percent: number) => void
): Promise<Uint8Array | null> {
  const response = await api.fetch(url, authToken, identityProvider);

  if (!response.ok) throw await responseError(response, ErrorMessages.DownloadFailed);

  const contentLength = response.headers.get("Content-Length");
  if (!contentLength) {
    console.warn("Server did not provide Content-Length. No progress possible.");
    return null;
  }

  const total = parseInt(contentLength, 10);
  let loaded = 0;

  if (!response.body) throw new Error("Response body is empty");

  const reader = response.body.getReader();
  const chunks = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    chunks.push(value);
    loaded += value.length;

    const percent = ((loaded / total) * 100).toFixed(2);
    console.log(`Downloaded ${percent}%`);
    setPercent(parseFloat(percent));
  }

  // combine chunks into a single Uint8Array
  const allChunks = new Uint8Array(loaded);
  let position = 0;
  for (const chunk of chunks) {
    allChunks.set(chunk, position);
    position += chunk.length;
  }
  return allChunks;
};

const ReportsTree: React.FC<ReportsTreeProps> = ({ authToken, identityProvider, shouldRefresh }) => {
  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);
  const [areReportsLoading, setAreReportsLoading] = useState(false);
  const [isRemoveConfirmationOpen, setIsRemoveConfirmationOpen] = useState(false);
  const [isRemoveTemplateConfirmationOpen, setIsRemoveTemplateConfirmationOpen] = useState(false);
  const [currentReportInstance, setCurrentReportInstance] = useState<ReportInstance>();
  const [currentReportTemplate, setCurrentReportTemplate] = useState<ReportTemplate>();
  const [isReportDownloading, setIsReportDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [expandedGroupIds, setExpandedGroupIds] = useState<string[]>(["myAnalyzers", "systemAnalyzers", "otherAnalyzers"]);
  const [error, setError] = useState<string>("");
  const [percent, setPercent] = useState<number>(0);
  const [deletingReportInstanceIds, setDeletingReportInstanceIds] = useState<string[]>([]);
  const [deletingTemplateIds, setDeletingTemplateIds] = useState<string[]>([]);
  const deletingReportInstanceIdsRef = useRef<Set<string>>(new Set());
  const deletingTemplateIdsRef = useRef<Set<string>>(new Set());

  const styles = useStyles();
  useEffect(() => {
    // Do not fetch without a usable token: the first mount can run with authToken
    // still empty (see the Okta path in LoginButton.oktaLoginCompleted), and the
    // failed request would then only be retried on an explicit Refresh.
    if (!authToken) return;
    const fetchReportTemplates = async () => {
      try {
        setAreReportsLoading(true);
        const response = await api.fetch("reports", authToken, identityProvider);
        if (!response.ok) throw await responseError(response, ErrorMessages.LoadReportsFailed);
        const data: ApiReportTemplate[] = await response.json();
        const reportTemplates = data.map((row: ApiReportTemplate) => ({
          id: row.id,
          name: row.name,
          url: row.url,
          token: row.sasToken,
          isUserTemplate: row.isUserTemplate === true,
          isSystemTemplate: row.isSystemTemplate === true,
          instances:
            row.excelFiles?.map((row: ApiReportInstance) => ({
              id: row.id,
              name: row.name,
              url: row.url,
              token: row.sasToken,
            })) || [],
        }));
        setReportTemplates(reportTemplates);
      } catch (error) {
        setError("Error fetching report templates: " + error);
      } finally {
        setAreReportsLoading(false);
      }
    };
    fetchReportTemplates();
  }, [shouldRefresh, authToken, identityProvider]);

  const handleToggle = (nodeId: string) => {
    setSearchQuery("");
    setExpandedNodeId((prevExpandedNodeId) => (prevExpandedNodeId === nodeId ? null : nodeId));
  };

  const handleGroupToggle = (groupId: string) => {
    setExpandedGroupIds((prevExpandedGroupIds) =>
      prevExpandedGroupIds.includes(groupId)
        ? prevExpandedGroupIds.filter((expandedGroupId) => expandedGroupId !== groupId)
        : [...prevExpandedGroupIds, groupId]
    );
  };

  const sortedReportTemplates = [...reportTemplates].sort((a, b) => a.name.localeCompare(b.name));
  const reportTemplateGroups: ReportTemplateGroup[] = [
    {
      id: "myAnalyzers",
      name: "My Analyzers",
      templates: sortedReportTemplates.filter((reportTemplate) => reportTemplate.isUserTemplate),
    },
    {
      id: "systemAnalyzers",
      name: "Standard Analyzers",
      templates: sortedReportTemplates.filter((reportTemplate) => !reportTemplate.isUserTemplate && reportTemplate.isSystemTemplate),
    },
    {
      id: "otherAnalyzers",
      name: "Other Analyzers",
      templates: sortedReportTemplates.filter(
        (reportTemplate) => !reportTemplate.isUserTemplate && !reportTemplate.isSystemTemplate
      ),
    },
  ];

  const handleReportTemplateClick = async (e: React.MouseEvent<HTMLAnchorElement>, reportTemplate: ReportTemplate) => {
    e.preventDefault();
    setIsReportDownloading(true);
    console.log("Opening report: " + reportTemplate.id);
    try {
      await fetchAndOpenReportTemplate(
        "reports/template/" + reportTemplate.id,
        authToken,
        identityProvider,
        setPercent
      );
    } catch (error) {
      setError(String(error));
    }
    setTimeout(() => {
      setIsReportDownloading(false);
    }, 1000);
  };
  const handleReportClick = async (e: React.MouseEvent<HTMLAnchorElement>, reportInstance: ReportInstance) => {
    e.preventDefault();
    setIsReportDownloading(true);
    console.log("Opening report instance: " + reportInstance.id);
    try {
      await fetchAndOpenReportInstance(
        "reports/file/" + reportInstance.id,
        authToken,
        identityProvider,
        setPercent
      );
    } catch (error) {
      setError(String(error));
    }
    setTimeout(() => {
      setIsReportDownloading(false);
    }, 1000);
  };
  const handleRemoveClick = (reportInstance: ReportInstance) => {
    if (deletingReportInstanceIdsRef.current.has(reportInstance.id)) return;

    setCurrentReportInstance(reportInstance);
    setIsRemoveConfirmationOpen(true);
  };
  const handleRemoveTemplateClick = (reportTemplate: ReportTemplate) => {
    if (deletingTemplateIdsRef.current.has(reportTemplate.id)) return;

    setCurrentReportTemplate(reportTemplate);
    setIsRemoveTemplateConfirmationOpen(true);
  };
  const handleRemoveConfirmationClick = async (reportInstance: ReportInstance) => {
    if (deletingReportInstanceIdsRef.current.has(reportInstance.id)) return;

    deletingReportInstanceIdsRef.current.add(reportInstance.id);
    setDeletingReportInstanceIds(Array.from(deletingReportInstanceIdsRef.current));

    try {
      const response = await api.delete(
        `report/file?ReportFileId=${encodeURIComponent(reportInstance.id)}`,
        authToken,
        identityProvider
      );
      if (!response.ok) throw await responseError(response, ErrorMessages.ReportRemoveFailed);

      console.log("Removed report instance: " + reportInstance.id);

      setReportTemplates((templates) =>
        templates.map((template) => ({
          ...template,
          instances: template.instances.filter((t) => t.id !== reportInstance.id),
        }))
      );
    } catch (error) {
      setError(getErrorMessage(error, ErrorMessages.ReportRemoveFailed));
    } finally {
      deletingReportInstanceIdsRef.current.delete(reportInstance.id);
      setDeletingReportInstanceIds(Array.from(deletingReportInstanceIdsRef.current));
      setIsRemoveConfirmationOpen(false);
    }
  };
  const handleRemoveTemplateConfirmationClick = async (reportTemplate: ReportTemplate) => {
    if (!reportTemplate.isUserTemplate) {
      setIsRemoveTemplateConfirmationOpen(false);
      return;
    }
    if (deletingTemplateIdsRef.current.has(reportTemplate.id)) return;

    deletingTemplateIdsRef.current.add(reportTemplate.id);
    setDeletingTemplateIds(Array.from(deletingTemplateIdsRef.current));

    try {
      const response = await api.delete(
        `template/${encodeURIComponent(reportTemplate.id)}`,
        authToken,
        identityProvider
      );
      // BuilderMetrix returns an actionable 400 here — e.g. that saved Excel files are still
      // associated with the template — so its message is what the user needs to see.
      if (!response.ok) throw await responseError(response, ErrorMessages.TemplateRemoveFailed);

      console.log("Removed report template: " + reportTemplate.id);

      setReportTemplates((templates) => templates.filter((template) => template.id !== reportTemplate.id));
    } catch (error) {
      setError(getErrorMessage(error, ErrorMessages.TemplateRemoveFailed));
    } finally {
      deletingTemplateIdsRef.current.delete(reportTemplate.id);
      setDeletingTemplateIds(Array.from(deletingTemplateIdsRef.current));
      setIsRemoveTemplateConfirmationOpen(false);
    }
  };
  const handleRemoveCancelClick = () => {
    setIsRemoveConfirmationOpen(false);
  };
  const handleRemoveTemplateCancelClick = () => {
    setIsRemoveTemplateConfirmationOpen(false);
  };

  return (
    <div>
      {areReportsLoading && <div style={{ marginBottom: "8px" }}>Loading...</div>}
      <Tree aria-label="Reports tree">
        {reportTemplateGroups.map((reportTemplateGroup) => (
          <TreeItem
            itemType="branch"
            key={reportTemplateGroup.id}
            onOpenChange={() => handleGroupToggle(reportTemplateGroup.id)}
            open={expandedGroupIds.includes(reportTemplateGroup.id)}
          >
            <TreeItemLayout>
              <span className={styles.groupLabel}>{reportTemplateGroup.name}</span>
            </TreeItemLayout>
            <Tree>
              {reportTemplateGroup.templates.length === 0 ? (
                <TreeItem itemType="leaf">
                  <TreeItemLayout>
                    <span className={styles.noAnalyzers}>No Analyzers</span>
                  </TreeItemLayout>
                </TreeItem>
              ) : (
                reportTemplateGroup.templates.map((reportTemplate) => {
                  const isTemplateDeleting = deletingTemplateIds.includes(reportTemplate.id);

                  return (
                    <TreeItem
                      itemType={reportTemplate.instances.length > 0 ? "branch" : "leaf"}
                      key={reportTemplate.id}
                      onOpenChange={() => handleToggle(reportTemplate.id)}
                      open={expandedNodeId === reportTemplate.id}
                    >
                      <TreeItemLayout
                        className={styles.templateItemLayout}
                        iconBefore={
                          <Tooltip
                            content={reportTemplate.isSystemTemplate ? "System template" : "User template"}
                            relationship="label"
                          >
                            {reportTemplate.isSystemTemplate ? (
                              <img className={styles.appIcon} src="assets/icon-32.png" alt="" />
                            ) : (
                              <Person20Regular className={styles.templateIcon} />
                            )}
                          </Tooltip>
                        }
                        actions={
                          reportTemplate.isUserTemplate ? (
                            <FluentButton
                              appearance="subtle"
                              icon={isTemplateDeleting ? <Spinner size="tiny" /> : <Delete20Regular />}
                              aria-label={isTemplateDeleting ? "Deleting template" : "Delete template"}
                              title={isTemplateDeleting ? "Deleting template" : "Delete template"}
                              disabled={isTemplateDeleting}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveTemplateClick(reportTemplate);
                              }}
                            />
                          ) : undefined
                        }
                      >
                        <Link className={styles.reportTemplate} onClick={(e) => handleReportTemplateClick(e, reportTemplate)}>
                          {fileName(reportTemplate.name)}
                        </Link>
                      </TreeItemLayout>
                      <Tree>
                        <div style={{ paddingLeft: "48px" }}>
                          <Input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search"
                            value={searchQuery}
                            size="small"
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                          <div style={{ maxHeight: "100px", overflow: "auto" }}>
                            {reportTemplate.instances
                              .filter(
                                (
                                  reportInstance //reportInstance?.name?.toLowerCase().includes(searchQuery.toLowerCase())
                                ) => fileName(reportInstance.name).toLowerCase().includes(searchQuery.toLowerCase())
                              )
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((reportInstance) => {
                                const isReportInstanceDeleting = deletingReportInstanceIds.includes(reportInstance.id);

                                return (
                                  <TreeItem itemType="leaf" key={reportInstance.id}>
                                    <TreeItemLayout
                                      style={{ paddingLeft: "4px" }}
                                      actions={
                                        <FluentButton
                                          appearance="subtle"
                                          icon={isReportInstanceDeleting ? <Spinner size="tiny" /> : <Delete20Regular />}
                                          aria-label={isReportInstanceDeleting ? "Deleting report" : "Delete report"}
                                          title={isReportInstanceDeleting ? "Deleting report" : "Delete report"}
                                          disabled={isReportInstanceDeleting}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleRemoveClick(reportInstance);
                                          }}
                                        />
                                      }
                                    >
                                      <Link
                                        className={styles.reportInstance}
                                        onClick={(e) => handleReportClick(e, reportInstance)}
                                      >
                                        {fileName(reportInstance.name)}
                                      </Link>
                                    </TreeItemLayout>
                                  </TreeItem>
                                );
                              })}
                          </div>
                        </div>
                      </Tree>
                    </TreeItem>
                  );
                })
              )}
            </Tree>
          </TreeItem>
        ))}
      </Tree>
      <ConfirmationForm
        isOpen={isRemoveConfirmationOpen}
        onConfirm={() => currentReportInstance && handleRemoveConfirmationClick(currentReportInstance)}
        onCancel={handleRemoveCancelClick}
        dialogTitle="Remove Report"
        dialogContent="Are you sure you want to remove the report?"
        confirmButtonLabel="Remove"
        confirmingButtonLabel="Removing report..."
        cancelButtonLabel="Cancel"
        isConfirming={currentReportInstance ? deletingReportInstanceIds.includes(currentReportInstance.id) : false}
      />
      <ConfirmationForm
        isOpen={isRemoveTemplateConfirmationOpen}
        onConfirm={() => currentReportTemplate && handleRemoveTemplateConfirmationClick(currentReportTemplate)}
        onCancel={handleRemoveTemplateCancelClick}
        dialogTitle="Remove Template"
        dialogContent="Are you sure you want to remove the template?"
        confirmButtonLabel="Remove"
        confirmingButtonLabel="Removing template..."
        cancelButtonLabel="Cancel"
        isConfirming={currentReportTemplate ? deletingTemplateIds.includes(currentReportTemplate.id) : false}
      />
      {isReportDownloading && (
        <div className={styles.spinnerContainer}>
          <Spinner size="large" />
          {percent > 0 && (
            <p style={{ color: "white", marginLeft: "20px" }}>Downloading... {`${percent.toFixed(0)}%`}</p>
          )}
        </div>
      )}
      <div style={{ marginTop: "20px", borderTop: "1px solid #f0f0f0", paddingTop: "10px" }}>
        <p>
          Click on a BuilderMetrix® Analyzer to open it in a new window and use the BuilderMetrix® button (Show
          Analyzer Pane) to set parameters and get the latest data.
        </p>
      </div>
      <Dialog open={(error?.length ?? 0) > 0}>
        <DialogSurface style={{ margin: "16px" }}>
          <DialogBody>
            <DialogTitle>Error</DialogTitle>
            <DialogContent>
              <p style={{ color: "red" }}>
                An error has occurred:
                <br />
                {error}
              </p>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button onClick={() => setError("")} label="Close" />
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ReportsTree;
